import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { BatchDetail, BatchItemView, ItemStatus, LogEntry } from '@ia/shared';
import { api, ApiError, errorMessage } from '../api';
import { Alert, ConfirmDialog, Empty, ItemPill, KindTag, Spinner, StatusPill, When } from '../components/ui';
import { duration, formatDate, usePolling } from '../util';
import { Counts, destLabel } from './Batches';

export function BatchDetailPage() {
  const { id = '' } = useParams();
  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [replayBusy, setReplayBusy] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [replayNotice, setReplayNotice] = useState<string | null>(null);
  const [processOpen, setProcessOpen] = useState(false);
  const [processBusy, setProcessBusy] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [processNotice, setProcessNotice] = useState<string | null>(null);
  const [workerBusy, setWorkerBusy] = useState(false);
  const [workerError, setWorkerError] = useState<string | null>(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [checkNotice, setCheckNotice] = useState<string | null>(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactsBusy, setContactsBusy] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [contactsNotice, setContactsNotice] = useState<string | null>(null);

  const isPending = batch?.bucket === 'pending';
  // Client data is people only: no policies to replay, and the full import is the client worker's job, not name-only contacts.
  const clientData = batch?.kind === 'client_data';

  usePolling(
    async (signal) => {
      try {
        const b = await api.batch(id, signal);
        setBatch(b);
        setError(null);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(errorMessage(e));
      }
    },
    3000,
    batch === null || isPending,
    [id],
  );

  const doReplay = async () => {
    setReplayBusy(true);
    setReplayError(null);
    try {
      const b = await api.replay(id);
      setBatch(b);
      setConfirmOpen(false);
      setReplayNotice('Replay queued. This page refreshes automatically while it runs.');
    } catch (e) {
      let msg = errorMessage(e);
      if (e instanceof ApiError && e.status === 409) msg = `Batch is already pending or processing — ${msg}`;
      if (e instanceof ApiError && e.status === 412) msg = `Destination has no PAT configured — ${msg}`;
      setReplayError(msg);
    } finally {
      setReplayBusy(false);
    }
  };

  const doProcess = async () => {
    setProcessBusy(true);
    setProcessError(null);
    try {
      const b = await api.process(id);
      setBatch(b);
      setProcessOpen(false);
      setProcessNotice(`Sent to ${b.processedBlobPath}. The DataHub worker imports it on its next run.`);
    } catch (e) {
      setProcessError(errorMessage(e));
    } finally {
      setProcessBusy(false);
    }
  };

  const doWorkerCheck = async () => {
    setWorkerBusy(true);
    setWorkerError(null);
    try {
      setBatch(await api.workerResult(id));
    } catch (e) {
      setWorkerError(errorMessage(e));
    } finally {
      setWorkerBusy(false);
    }
  };

  const doCheckContacts = async () => {
    setCheckBusy(true);
    setCheckError(null);
    setCheckNotice(null);
    try {
      const { result, ...b } = await api.checkContacts(id);
      setBatch(b);
      const parts = [`${result.inMaximizer} in Maximizer`, `${result.missing} missing`];
      if (result.failed) parts.push(`${result.failed} could not be checked (see the logs)`);
      setCheckNotice(`Checked ${result.checked} client${result.checked === 1 ? '' : 's'}: ${parts.join(', ')}.`);
    } catch (e) {
      setCheckError(errorMessage(e));
    } finally {
      setCheckBusy(false);
    }
  };

  const doCreateContacts = async () => {
    setContactsBusy(true);
    setContactsError(null);
    try {
      const { result, ...b } = await api.createContacts(id);
      setBatch(b);
      setContactsOpen(false);
      const parts = [`${result.created} created`];
      if (result.matched) parts.push(`${result.matched} found in Maximizer meanwhile`);
      if (result.failed) parts.push(`${result.failed} failed (see the logs)`);
      setContactsNotice(`Contacts: ${parts.join(', ')}.`);
    } catch (e) {
      setContactsError(errorMessage(e));
    } finally {
      setContactsBusy(false);
    }
  };

  const missing = batch?.contacts.missing ?? 0;
  const createLabel = `Create ${missing} contact${missing === 1 ? '' : 's'}`;
  const sending = batch?.items.filter((i) => !i.excluded).length ?? 0;

  if (notFound)
    return (
      <div className="page">
        <p>
          <Link to="/batches">← Batches</Link>
        </p>
        <Alert kind="warn">Batch {id} was not found.</Alert>
      </div>
    );
  if (!batch)
    return (
      <div className="page">
        {error ? <Alert>{error}</Alert> : <Spinner />}
      </div>
    );

  return (
    <div className="page">
      <p className="crumbs">
        <Link to="/batches">← Batches</Link>
      </p>
      <div className="page-head">
        <div>
          <h1 className="with-pill">
            Batch <code className="id">{batch.id}</code> <StatusPill status={batch.status} /> <KindTag kind={batch.kind} />
          </h1>
          <div className="muted small">
            Created {formatDate(batch.createdAt)} · updated {formatDate(batch.updatedAt)}
            {isPending && <span className="live-dot"> live</span>}
          </div>
        </div>
        <div className="head-actions">
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!batch.canProcess}
              aria-describedby={!batch.canProcess && batch.processBlockedReason ? 'process-blocked' : undefined}
              onClick={() => {
                setProcessError(null);
                setProcessOpen(true);
              }}
            >
              Process {sending}
              {clientData && ` client${sending === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              className="btn"
              disabled={!batch.canCheckContacts || checkBusy}
              onClick={() => void doCheckContacts()}
            >
              {checkBusy ? 'Checking…' : 'Check contacts'}
            </button>
            {!clientData && (
              <button
                type="button"
                className="btn"
                disabled={!batch.canCreateContacts}
                aria-describedby={!batch.canCreateContacts && missing > 0 ? 'contacts-blocked' : undefined}
                onClick={() => {
                  setContactsError(null);
                  setContactsOpen(true);
                }}
              >
                {createLabel}
              </button>
            )}
            {!clientData && (
              <button
                type="button"
                className="btn"
                disabled={!batch.canReplay}
                aria-describedby={!batch.canReplay && batch.replayBlockedReason ? 'replay-blocked' : undefined}
                onClick={() => {
                  setReplayError(null);
                  setConfirmOpen(true);
                }}
              >
                Replay
              </button>
            )}
          </div>
          {!batch.canProcess && batch.processBlockedReason && (
            <div id="process-blocked" className="blocked-reason">
              Process: {batch.processBlockedReason}
            </div>
          )}
          {!clientData && !batch.canCreateContacts && missing > 0 && batch.createContactsBlockedReason && (
            <div id="contacts-blocked" className="blocked-reason">
              Create contacts: {batch.createContactsBlockedReason}
            </div>
          )}
          {!clientData && !batch.canReplay && batch.replayBlockedReason && (
            <div id="replay-blocked" className="blocked-reason">
              Replay: {batch.replayBlockedReason}
            </div>
          )}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
      {processNotice && <Alert kind="ok">{processNotice}</Alert>}
      {processError && !processOpen && <Alert>{processError}</Alert>}
      {workerError && <Alert kind="warn">{workerError}</Alert>}
      {checkNotice && <Alert kind="ok">{checkNotice}</Alert>}
      {checkError && <Alert>{checkError}</Alert>}
      {contactsNotice && <Alert kind="ok">{contactsNotice}</Alert>}
      {contactsError && !contactsOpen && <Alert>{contactsError}</Alert>}
      {replayNotice && <Alert kind="ok">{replayNotice}</Alert>}
      {replayError && !confirmOpen && <Alert>{replayError}</Alert>}
      {batch.lastError && batch.bucket === 'failed' && (
        <Alert>
          <strong>Last error:</strong> {batch.lastError}
        </Alert>
      )}

      <div className="cards">
        <section className="card">
          <h2>Destination</h2>
          <dl className="kv">
            <dt>Tenant</dt>
            <dd>
              <Link to="/destinations">{destLabel(batch.destination)}</Link>
            </dd>
            <dt>Database</dt>
            <dd>
              <code>{batch.destination.database}</code>
            </dd>
            <dt>Maximizer user</dt>
            <dd>
              {batch.maximizerUser.displayName ?? '—'} <span className="muted small">({batch.maximizerUser.uid})</span>
            </dd>
          </dl>
        </section>
        <section className="card">
          <h2>Source</h2>
          <dl className="kv">
            <dt>Type</dt>
            <dd>
              <KindTag kind={batch.kind} />
            </dd>
            <dt>Portal</dt>
            <dd>
              {batch.portalKey} <span className="muted small">{batch.portalOrigin}</span>
            </dd>
            <dt>Portal user</dt>
            <dd>{batch.portalUser ?? '—'}</dd>
            <dt>Recipe</dt>
            <dd>
              {batch.recipeId} <span className="muted small">v{batch.recipeVersion}</span>
            </dd>
            <dt>Captured</dt>
            <dd>
              <When iso={batch.capturedAt} inline />
            </dd>
            <dt>Extension</dt>
            <dd>{batch.extensionVersion ?? '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <h2>Results</h2>
          <dl className="kv">
            <dt>Items</dt>
            <dd>
              <Counts c={batch.counts} />
            </dd>
            <dt>Contacts</dt>
            <dd>
              <ContactCounts c={batch.contacts} />
            </dd>
            <dt>Runs</dt>
            <dd>{batch.runCount}</dd>
            <dt>Sent to storage</dt>
            <dd>
              {batch.processedAt ? (
                <>
                  <When iso={batch.processedAt} inline /> <code className="small break">{batch.processedBlobPath}</code>
                </>
              ) : (
                '—'
              )}
            </dd>
            <dt>Worker</dt>
            <dd>
              {batch.workerRun ? (
                <WorkerRunSummary run={batch.workerRun} />
              ) : batch.processedAt ? (
                <span className="muted">Waiting for the worker to pick the zip up (checked every 30 s)</span>
              ) : (
                '—'
              )}
              {batch.processedAt && (
                <>
                  {' '}
                  <button type="button" className="btn btn-ghost btn-sm" disabled={workerBusy} onClick={() => void doWorkerCheck()}>
                    {workerBusy ? 'Checking…' : batch.workerRun ? 'Re-read' : 'Check now'}
                  </button>
                </>
              )}
            </dd>
          </dl>
          <div className="muted small">Succeeded / failed / skipped of total</div>
        </section>
      </div>

      <ItemsSection items={batch.items} policies={!clientData} />
      <RunsSection batch={batch} />
      <LogsPanel batch={batch} live={isPending} />

      <ConfirmDialog
        open={confirmOpen}
        title="Replay this batch?"
        confirmLabel="Replay batch"
        busy={replayBusy}
        error={replayError}
        onConfirm={() => void doReplay()}
        onClose={() => setConfirmOpen(false)}
      >
        <p>
          The batch will be re-run against <strong>{destLabel(batch.destination)}</strong> using the destination's
          personal access token (PAT), not the advisor's session.
        </p>
        <p>
          Replay is <strong>idempotent</strong>: policies that were already imported are updated in place or skipped
          if unchanged. Contacts and policies are never duplicated.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={processOpen}
        title="Send this batch for processing?"
        confirmLabel="Process batch"
        busy={processBusy}
        error={processError}
        onConfirm={() => void doProcess()}
        onClose={() => setProcessOpen(false)}
      >
        {clientData ? (
          <p>
            The parsed clients (excluded ones left out) are handed to the client import worker for{' '}
            <strong>{destLabel(batch.destination)}</strong>, which creates or updates them in the Maximizer address book on its next run;
            from there progress is tracked by the worker, not on this page.
          </p>
        ) : (
          <p>
            The parsed records (excluded clients left out) are written as policies, coverages, clients and beneficiaries
            CSVs plus metadata into the <strong>data-sync</strong> container for{' '}
            <strong>{destLabel(batch.destination)}</strong>. The DataHub InsurancesWorker imports them into Maximizer on
            its next run; from there progress is tracked by the worker, not on this page.
          </p>
        )}
        {!clientData && missing > 0 && (
          <Alert kind="warn">
            {missing} client{missing === 1 ? ' is' : 's are'} not in Maximizer. The worker only files policies under an existing address book
            entry, so it will <strong>not create policies</strong> for {missing === 1 ? 'that client' : 'those clients'}. Use{' '}
            <strong>{createLabel}</strong> first if they should be imported.
          </Alert>
        )}
        {batch.processedAt && (
          <p>
            This batch was already sent <When iso={batch.processedAt} inline />. Sending again creates a new upload.
          </p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={contactsOpen}
        title={`${createLabel} in Maximizer?`}
        confirmLabel={createLabel}
        busy={contactsBusy}
        error={contactsError}
        onConfirm={() => void doCreateContacts()}
        onClose={() => setContactsOpen(false)}
      >
        <p>
          The {missing} client{missing === 1 ? '' : 's'} marked <span className="tag tag-missing">Missing</span> will be created in{' '}
          <strong>{destLabel(batch.destination)}</strong> as Individuals with just their first and last name, signed in as the Insurance
          integration user whose token the DataHub worker uses. Each one is looked up once more first, so a contact added in the meantime
          is matched, not duplicated. They can be converted to Contacts under a household in Maximizer afterwards.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function WorkerRunSummary({ run }: { run: NonNullable<BatchDetail['workerRun']> }) {
  const t = run.totals;
  const skipped = t ? t.invalid + t.unmatched + t.octopusFailed : 0;
  return (
    <span>
      <ItemPill status={run.status === 'processed' ? 'succeeded' : 'failed'} />{' '}
      {t && (
        <span title={`${t.created} created, ${t.unmatched} unmatched (no address book entry), ${t.invalid} invalid, ${t.octopusFailed} failed in Octopus, of ${t.total}`}>
          <span className="text-processed">{t.created} created</span>
          {skipped > 0 && (
            <>
              <span className="sep">·</span>
              <span className="text-pending">{skipped} not created</span>
            </>
          )}
          <span className="muted small"> of {t.total}</span>
        </span>
      )}
      {run.completedAt && (
        <>
          {' '}
          <When iso={run.completedAt} inline />
        </>
      )}
      {run.errorMessage && <div className="text-failed small">{run.errorMessage}</div>}
      {run.note && <div className="muted small">{run.note}</div>}
    </span>
  );
}

function ContactCounts({ c }: { c: BatchDetail['contacts'] }) {
  const found = c.inMaximizer + c.created;
  return (
    <span className="counts" title={`${c.inMaximizer} matched, ${c.created} created here, ${c.missing} missing, ${c.unchecked} not checked`}>
      <span className="text-processed">{found} in Maximizer</span>
      <span className="sep">·</span>
      <span className={c.missing ? 'text-pending' : 'muted'}>{c.missing} missing</span>
      {c.unchecked > 0 && <span className="muted small"> · {c.unchecked} not checked</span>}
    </span>
  );
}

type ClientGroup = { key: string; clientName: string | null; items: BatchItemView[] };

/** One row per person: a client with several policies arrives as several records (one each). Unparsed records stand alone. */
function groupByClient(items: BatchItemView[]): ClientGroup[] {
  const groups = new Map<string, ClientGroup>();
  for (const it of items) {
    const key = it.clientKey ?? `record:${it.id}`;
    const g = groups.get(key);
    if (g) g.items.push(it);
    else groups.set(key, { key, clientName: it.clientName, items: [it] });
  }
  return [...groups.values()];
}

/** Client data: one row per household, its members under it; a person without a household stands alone. */
function groupByHousehold(items: BatchItemView[]): ClientGroup[] {
  const groups = new Map<string, ClientGroup>();
  for (const it of items) {
    const key = it.household ? `household:${it.household.id ?? it.household.name}` : (it.clientKey ?? `record:${it.id}`);
    const g = groups.get(key);
    if (g) g.items.push(it);
    else groups.set(key, { key, clientName: it.household?.name ?? it.clientName, items: [it] });
  }
  return [...groups.values()];
}

function groupStatus(items: BatchItemView[]): ItemStatus {
  const live = items.filter((i) => !i.excluded);
  if (!live.length) return 'skipped';
  if (live.some((i) => i.status === 'failed')) return 'failed';
  if (live.every((i) => i.status === 'succeeded')) return 'succeeded';
  if (live.every((i) => i.status === 'skipped')) return 'skipped';
  return 'pending';
}

function ContactCell({ it }: { it: BatchItemView }) {
  if (it.contactAction === 'match')
    return (
      <>
        <span className="tag tag-match">In Maximizer</span> <code className="small">{it.abEntryKey}</code>
      </>
    );
  if (it.contactAction === 'create' && it.abEntryKey)
    return (
      <>
        <span className="tag tag-create">Created</span> <code className="small">{it.abEntryKey}</code>
      </>
    );
  if (it.contactAction === 'create')
    return (
      <span className="tag tag-missing" title="No address book entry matches this client's name and email; the worker will skip their policies">
        Missing
      </span>
    );
  return (
    <span className="muted" title="Not checked (the record was not parsed, or the preview has not run)">
      —
    </span>
  );
}

/** `policies` = show the policy columns; off for client data, which carries none and is grouped by household instead. */
function ItemsSection({ items, policies }: { items: BatchItemView[]; policies: boolean }) {
  const households = !policies;
  const groups = households ? groupByHousehold(items) : groupByClient(items);
  const policyCount = items.reduce((n, i) => n + i.policies.length, 0);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  return (
    <section className="section">
      <div className="section-head">
        <h2>
          {households ? 'Households' : 'Clients'} ({groups.length}){' '}
          <span className="muted small">
            {households && <> · {items.length} contact{items.length === 1 ? '' : 's'}</>}
            {!households && <> · {items.length} record{items.length === 1 ? '' : 's'}</>}
            {policies && <> · {policyCount} polic{policyCount === 1 ? 'y' : 'ies'}</>}
          </span>
        </h2>
        {groups.length > 0 && (
          <div className="btn-row">
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(new Set(groups.map((g) => g.key)))}>
              Expand all
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(new Set())}>
              Collapse all
            </button>
          </div>
        )}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th aria-label="Expand" className="w-expander" />
              <th>{households ? 'Household' : 'Client'}</th>
              <th className="num">{households ? 'Contacts' : 'Records'}</th>
              <th>{households ? 'Primary' : 'Contact'}</th>
              <th>Parser</th>
              {policies && <th className="num">Policies</th>}
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const isOpen = open.has(g.key);
              const first = (households ? g.items.find((i) => /primary|head|main/i.test(i.household?.role ?? '')) : undefined) ?? g.items.find((i) => i.contactAction) ?? g.items[0];
              const parsers = [...new Set(g.items.map((i) => i.parser).filter(Boolean))] as string[];
              const errors = g.items.filter((i) => i.error);
              const allExcluded = g.items.every((i) => i.excluded);
              const label = g.clientName ?? g.items[0].externalId;
              return (
                <Fragment key={g.key}>
                  <tr className={allExcluded ? 'excluded' : undefined}>
                    <td>
                      <button
                        type="button"
                        className="expander"
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Hide' : 'Show'} records for ${label}`}
                        onClick={() => toggle(g.key)}
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                    </td>
                    <td>
                      {g.clientName ?? <span className="muted">—</span>}
                      {households && first.household?.id && <code className="small"> {first.household.id}</code>}
                    </td>
                    <td className="num" title={g.items.map((i) => i.clientName ?? i.externalId).join(', ')}>
                      {g.items.length}
                    </td>
                    <td>
                      {households && first.clientName && <span>{first.clientName} </span>}
                      <ContactCell it={first} />
                    </td>
                    <td>
                      {parsers.length ? parsers.map((p) => (
                        <span key={p} className={`tag tag-${p}`}>
                          {p}
                        </span>
                      )) : <span className="muted">—</span>}
                    </td>
                    {policies && <td className="num">{g.items.reduce((n, i) => n + i.policies.length, 0)}</td>}
                    <td>
                      <ItemPill status={groupStatus(g.items)} />
                      {allExcluded && <span className="tag">excluded</span>}
                    </td>
                    <td className="error-cell">
                      {errors.length ? (
                        <>
                          {errors[0].error}
                          {errors.length > 1 && <span className="muted small"> (+{errors.length - 1} more)</span>}
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="subrow">
                      <td />
                      <td colSpan={policies ? 7 : 6}>
                        <table className="table table-inner">
                          <thead>
                            <tr>
                              {households && (
                                <>
                                  <th>Contact</th>
                                  <th>Role</th>
                                  <th>In Maximizer</th>
                                </>
                              )}
                              <th>Record</th>
                              <th>Parser</th>
                              {policies && (
                                <>
                                  <th>Policy #</th>
                                  <th>Carrier</th>
                                  <th>Action</th>
                                  <th>CRM key</th>
                                </>
                              )}
                              <th>Status</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.items.flatMap((it) =>
                              (policies && it.policies.length ? it.policies : [null]).map((p, idx) => (
                                <tr key={`${it.id}:${p ? `${p.carrierCode}:${p.policyNumber}` : idx}`} className={it.excluded ? 'excluded' : undefined}>
                                  {households && (
                                    <>
                                      <td>{it.clientName ?? <span className="muted">—</span>}</td>
                                      <td>{it.household?.role ? <span className="tag">{it.household.role}</span> : <span className="muted">—</span>}</td>
                                      <td>
                                        <ContactCell it={it} />
                                      </td>
                                    </>
                                  )}
                                  <td>
                                    <code>{it.externalId}</code>
                                    {it.excluded && <span className="tag">excluded</span>}
                                  </td>
                                  <td>{it.parser ? <span className={`tag tag-${it.parser}`}>{it.parser}</span> : <span className="muted">—</span>}</td>
                                  {policies && (
                                    <>
                                      <td>{p ? <code>{p.policyNumber}</code> : <span className="muted">no policies</span>}</td>
                                      <td>{p?.carrierCode ?? <span className="muted">—</span>}</td>
                                      <td>{p?.action ? <span className={`tag tag-${p.action}`}>{p.action}</span> : <span className="muted">—</span>}</td>
                                      <td>{p?.crmKey ? <code className="small">{p.crmKey}</code> : <span className="muted">—</span>}</td>
                                    </>
                                  )}
                                  <td>
                                    <ItemPill status={p ? p.status : it.status} />
                                  </td>
                                  <td className="error-cell">{(p ? p.error : it.error) ?? <span className="muted">—</span>}</td>
                                </tr>
                              )),
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {groups.length === 0 && <Empty>No records.</Empty>}
      </div>
    </section>
  );
}

function RunsSection({ batch }: { batch: BatchDetail }) {
  return (
    <section className="section">
      <h2>Runs ({batch.runs.length})</h2>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Run</th>
              <th>Trigger</th>
              <th>Auth</th>
              <th>Status</th>
              <th>Started</th>
              <th>Finished</th>
              <th>Duration</th>
              <th>Triggered by</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {batch.runs.map((r) => (
              <tr key={r.id}>
                <td>
                  <code className="small">{r.id}</code>
                </td>
                <td>
                  <span className={`tag tag-${r.trigger}`}>{r.trigger}</span>
                </td>
                <td>
                  <span className="tag" title={r.auth === 'session' ? "Advisor's live Maximizer session" : 'Destination personal access token'}>
                    {r.auth === 'session' ? 'session' : 'PAT'}
                  </span>
                </td>
                <td>
                  <ItemPill status={r.status} />
                </td>
                <td>
                  <When iso={r.startedAt} />
                </td>
                <td>
                  <When iso={r.finishedAt} />
                </td>
                <td className="small">{duration(r.startedAt, r.finishedAt)}</td>
                <td>{r.triggeredBy ?? <span className="muted">—</span>}</td>
                <td className="error-cell">{r.error ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {batch.runs.length === 0 && <Empty>No runs yet — the batch has not been approved for import.</Empty>}
      </div>
    </section>
  );
}

const LEVELS = ['debug', 'info', 'warn', 'error'] as const;

function logTime(at: string): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString(undefined, { hour12: false });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' })} ${time}`;
}
const MAX_LOGS = 5000;

function LogsPanel({ batch, live }: { batch: BatchDetail; live: boolean }) {
  const [runId, setRunId] = useState('');
  const [level, setLevel] = useState('');
  const [tail, setTail] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [openData, setOpenData] = useState<Set<number>>(new Set());
  const lastId = useRef<number | undefined>(undefined);
  const boxRef = useRef<HTMLDivElement>(null);
  const stickBottom = useRef(true);

  // Reset when filters change.
  useEffect(() => {
    setLogs([]);
    setLoaded(false);
    lastId.current = undefined;
  }, [batch.id, runId, level]);

  usePolling(
    async (signal) => {
      try {
        const rows = await api.batchLogs(batch.id, { runId: runId || undefined, level: level || undefined, after: lastId.current }, signal);
        if (rows.length) {
          lastId.current = rows[rows.length - 1]!.id;
          setLogs((prev) => {
            const merged = prev.concat(rows);
            return merged.length > MAX_LOGS ? merged.slice(merged.length - MAX_LOGS) : merged;
          });
        }
        setLoaded(true);
        setError(null);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setError(errorMessage(e));
      }
    },
    2000,
    live && tail,
    [batch.id, runId, level],
  );

  // Auto-scroll to the bottom in tail mode if the user hasn't scrolled up.
  useEffect(() => {
    const el = boxRef.current;
    if (el && tail && stickBottom.current) el.scrollTop = el.scrollHeight;
  }, [logs, tail]);

  const toggleData = (id: number) =>
    setOpenData((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <section className="section">
      <div className="section-head">
        <h2>Logs</h2>
        <div className="toolbar compact">
          <label className="inline-field">
            <span>Run</span>
            <select value={runId} onChange={(e) => setRunId(e.target.value)}>
              <option value="">All runs</option>
              {batch.runs.map((r, i) => (
                <option key={r.id} value={r.id}>
                  #{i + 1} {r.trigger} · {formatDate(r.startedAt)}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-field">
            <span>Level</span>
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="">All levels</option>
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l} and above
                </option>
              ))}
            </select>
          </label>
          <label className="check">
            <input type="checkbox" checked={tail} onChange={(e) => setTail(e.target.checked)} disabled={!live} />
            Tail {live ? '(every 2 s)' : '(batch not running)'}
          </label>
        </div>
      </div>
      {error && <Alert>{error}</Alert>}
      <div
        className="logs"
        ref={boxRef}
        tabIndex={0}
        role="log"
        aria-label="Batch logs"
        onScroll={(e) => {
          const el = e.currentTarget;
          stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {!loaded && <div className="muted">Loading logs…</div>}
        {loaded && logs.length === 0 && <div className="muted">No log entries{runId || level ? ' for these filters' : ''}.</div>}
        {logs.map((l) => (
          <div key={l.id} className={`log log-${l.level}`}>
            <span className="log-at" title={l.at}>
              {logTime(l.at)}
            </span>
            <span className="log-level">{l.level.toUpperCase().padEnd(5)}</span>
            <span className="log-msg">
              {l.message}
              {l.itemId && <span className="log-meta"> item={l.itemId}</span>}
              {l.data && (
                <button type="button" className="log-data-toggle" aria-expanded={openData.has(l.id)} onClick={() => toggleData(l.id)}>
                  {openData.has(l.id) ? '− data' : '+ data'}
                </button>
              )}
              {l.data && openData.has(l.id) && <pre className="log-data">{JSON.stringify(l.data, null, 2)}</pre>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
